import React, { createContext, useState, useEffect, useContext, useMemo } from "react";
import { Box, CircularProgress } from '@mui/material';
import { ICphDataContextType } from "../types/interfaces/ICphDataContextType";
import { ICphDataProviderProps } from "../types/interfaces/ICphDataProviderProps";
import { IIssue } from "../types/interfaces/IIssue";
import { useCphContext } from "./CphContext";
import { useApi } from "../hooks/useApi";
import { getKeyFromRoute } from "../../global/utilities/ProcessQueryString";

export const CphDataContext = createContext<ICphDataContextType | undefined>(undefined);

export const useCphDataContext = (): ICphDataContextType => {
    const context = useContext(CphDataContext);
    if (!context) {
        throw new Error('useCphDataContext must be used within a CphDataProvider');
    }
    return context;
};


export const CphDataProvider: React.FC<ICphDataProviderProps> = ({ children }) => {
    const [loadingCphDataCtx, setLoadingCphDataCtx] = useState<boolean>(true);
    const [filtersHydrated, setFiltersHydrated] = useState<boolean>(false);
    const [originalIssuesList, setOriginalIssuesList] = useState<IIssue[] | null>(null);
    const [filteredIssuesList, setFilteredIssuesList] = useState<IIssue[] | null>(null);
    const [selectedIssue, setSelectedIssue] = useState<IIssue | null>(null);
    const { loadingCphCtx, issuesList, issue, isSingleIssue, setIssue, setIsSingleIssueFlag, setSingleIssueKey } = useCphContext();

    const [showIssue, setShowIssue] = useState(false);
    const handleViewIssue = () => setShowIssue(true);
    
    const handleBack = () => {
        setShowIssue(false);
        setIssue(null);
        setSelectedIssue(null);
        if (isSingleIssue) {
            setIsSingleIssueFlag(false);
            setSingleIssueKey(undefined);
            // Remove the /{key} from the pathname
            const path = window.location.pathname;
            const key = getKeyFromRoute();
            const newPath = path.replace(new RegExp(`/${key}$`), '');
            if (newPath !== path) {
                window.history.replaceState({}, '', newPath + window.location.search + window.location.hash);
            }
        }
    };


    // Expose a method to get issues list using useApi with all options
    const getIssuesList = (
        url: string,
        options?: RequestInit,
        deps: any[] = [],
        method: 'GET' | 'POST' | 'DELETE' = 'GET'
    ) => {
        return useApi<IIssue | IIssue[] | null>(url, options, deps, method);
    };

    useEffect(() => {
        setFilteredIssuesList(issuesList ?? null);
        setOriginalIssuesList(issuesList ?? null);
        if (issuesList !== undefined) {
            setLoadingCphDataCtx(false);
        }
    }, [issuesList]);

    useEffect(() => {
        if (isSingleIssue && issue !== null) {
            setSelectedIssue(issue);
        }
    }, [isSingleIssue, issue]);

    const value = useMemo(() => ({
        filtersHydrated,
        originalIssuesList,
        filteredIssuesList,
        selectedIssue,
        showIssue,
        handleViewIssue,
        handleBack,
        setOriginalIssuesList,
        setFilteredIssuesList,
        setSelectedIssue,
        setShowIssue,
        getIssuesList,
        loadingCphDataCtx,
        setLoadingCphDataCtx,
        setFiltersHydrated
    }), [
        filtersHydrated,
        originalIssuesList,
        filteredIssuesList,
        selectedIssue,
        showIssue,
        handleViewIssue,
        handleBack,
        setOriginalIssuesList,
        setFilteredIssuesList,
        setSelectedIssue,
        setShowIssue,
        setLoadingCphDataCtx,
        setFiltersHydrated
    ]);

    return (
        <CphDataContext.Provider value={value}>
            {loadingCphCtx || loadingCphDataCtx ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                    <CircularProgress />
                </Box>
            ) : (
                children
            )}
        </CphDataContext.Provider>
    );
};

